import { ContentCourse, Question, Topic, Lesson, Module, Section, Activity } from '../modules/lcms/content-courses/schemas/schemas';

// Import JSON data
import questionBankData from './mock/viag/question-bank.json';
import contentCourseData from './mock/viag/content-course.json';
import templatesData from './mock/viag/templates.json';

// Template interface (minimal, since not in standard schema)
export interface Template {
  id: string;
  name: string;
  description: string;
}

// Properly typed question bank data
export const questionBank: Question[] = (questionBankData as any[]).map(q => ({
  question_id: q.question_id,
  question_text: q.question_text,
  question_type: q.question_type as 'multiple_choice' | 'fill_in_blank' | 'boolean' | 'text',
  options: q.options,
  correct_option: q.correct_option,
  correct_answer: q.correct_answer
}));

// Transform content course JSON to match ContentCourse schema
export const contentCourse: ContentCourse = {
  id: (contentCourseData as any).id,
  name: (contentCourseData as any).name,
  title: (contentCourseData as any).title,
  description: (contentCourseData as any).description,
  status: (contentCourseData as any).status,
  createdBy: (contentCourseData as any).createdBy,
  createdAt: (contentCourseData as any).createdAt,
  updatedAt: (contentCourseData as any).updatedAt,
  subject: (contentCourseData as any).subject,
  grade: (contentCourseData as any).grade,
  difficulty: (contentCourseData as any).difficulty,
  content: (contentCourseData as any).content,
  metadata: (contentCourseData as any).metadata,
  author: (contentCourseData as any).author,
  category: (contentCourseData as any).category,
  tags: (contentCourseData as any).tags,
  duration: (contentCourseData as any).duration,
  requiredForCompletion: (contentCourseData as any).requiredForCompletion,
  sequentialProgress: (contentCourseData as any).sequentialProgress,
  level: (contentCourseData as any).level,
  course_type: (contentCourseData as any).course_type as 'e-learning' | 'live-online' | 'offline' | 'combined',
  categories: (contentCourseData as any).categories,
  estimated_hours: (contentCourseData as any).estimated_hours,
  weeks: (contentCourseData as any).weeks,
  modules: ((contentCourseData as any).modules as any[]).map((module: any): Module => ({
    id: module.id,
    name: module.name,
    type: module.type as 'e-learning' | 'live-online' | 'offline' | 'combined',
    sections: (module.sections as any[]).map((section: any): Section => ({
      id: section.id,
      name: section.name,
      order: section.order,
      delivery_method: section.delivery_method as 'e-learning' | 'live-online' | 'offline' | 'combined',
      activities: (section.activities as any[]).map((activity: any): Activity => ({
        activity_id: activity.activity_id,
        type: activity.type as 'lesson' | 'exercise' | 'session',
        title: activity.title,
        default_platform: activity.default_platform,
        default_location: activity.default_location,
        questions: activity.questions?.map((q: any): Question => ({
          question_id: q.question_id,
          question_text: q.question_text,
          question_type: q.question_type as 'multiple_choice' | 'fill_in_blank' | 'boolean' | 'text',
          options: q.options,
          correct_option: q.correct_option,
          correct_answer: q.correct_answer
        }))
      }))
    })),
    additional_materials: module.additional_materials || [],
    resources: module.resources || []
  })),
  max_students: (contentCourseData as any).max_students,
  enrollment_deadline_offset_days: (contentCourseData as any).enrollment_deadline_offset_days,
  final_exam_type: (contentCourseData as any).final_exam_type,
  final_exam_duration_minutes: (contentCourseData as any).final_exam_duration_minutes,
  final_exam_passing_score: (contentCourseData as any).final_exam_passing_score,
  assignments: (contentCourseData as any).assignments,
  certificate_type: (contentCourseData as any).certificate_type,
  issue_on_completion: (contentCourseData as any).issue_on_completion,
  validity_period_months: (contentCourseData as any).validity_period_months,
  base_price: (contentCourseData as any).base_price,
  currency: (contentCourseData as any).currency
};

// Templates with proper typing
export const templates: Template[] = templatesData as Template[];

// Additional mock data for compatibility - Create topics from modules
export const mockTopics: Topic[] = ((contentCourseData as any).modules as any[]).map((module: any, index: number): Topic => ({
  id: index + 1,
  title: module.name,
  lessons: module.sections.flatMap((section: any) => 
    section.activities.map((activity: any, actIndex: number): Lesson => ({
      id: parseInt(activity.activity_id.replace('act-', '')),
      title: activity.title,
      type: activity.type === 'exercise' ? 'Exercise' : 'Lesson',
      status: 'Published',
      content: `Content for ${activity.title}`,
      questions: activity.questions?.map((q: any): Question => ({
        question_id: q.question_id,
        question_text: q.question_text,
        question_type: q.question_type as 'multiple_choice' | 'fill_in_blank' | 'boolean' | 'text',
        options: q.options,
        correct_option: q.correct_option,
        correct_answer: q.correct_answer
      }))
    }))
  )
}));

// Export raw JSON data for backward compatibility
export const rawQuestionBankData = questionBankData;
export const rawContentCourseData = contentCourseData;
export const rawTemplatesData = templatesData; 